class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  /**
   * Genera una variante spaziata del termine inserendo spazi
   * ai confini lettera→numero e numero→lettera (es. "PALL105" → "PALL 105").
   * Restituisce null se il termine ha già spazi o non ha tali transizioni.
   */
  getSpacedVariant(term) {
    if (term.includes(" ")) return null;

    const spaced = term
      .replace(/([a-zA-Z])(\d)/g, "$1 $2")
      .replace(/(\d)([a-zA-Z])/g, "$1 $2");

    return spaced !== term ? spaced : null;
  }

  /**
   * Controlla se un blocco di markup contiene almeno un risultato (<li>).
   */
  markupHasResults(markup) {
    if (!markup) return false;
    const doc = new DOMParser().parseFromString(`<body>${markup}</body>`, "text/html");
    return doc.querySelectorAll("li").length > 0;
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    // Se il termine non ha spazi ma ha transizioni lettera/numero,
    // riscrive la query con OR prima del submit (es. "PALL105 OR PALL 105").
    const query = this.getQuery();
    const spaced = this.getSpacedVariant(query);

    if (spaced) {
      event.preventDefault();
      this.input.value = `${query} OR ${spaced}`;
      this.querySelector("form.search").submit();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  /**
   * Costruisce l'URL per la fetch della predictive search.
   */
  buildSearchUrl(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;

    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    return `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`;
  }

  /**
   * Esegue una singola fetch e restituisce l'innerHTML della sezione risultati.
   */
  fetchMarkup(url) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        return new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
      });
  }

  /**
   * Fonde due blocchi di markup HTML deduplicando i <li> per href.
   * Gli elementi del markup secondario non già presenti nel primario
   * vengono aggiunti alla lista corrispondente (stesso id) o alla prima lista trovata.
   */
  mergeMarkup(primary, secondary) {
    if (!secondary) return primary;

    const parser = new DOMParser();
    const doc1 = parser.parseFromString(`<body>${primary}</body>`, "text/html");
    const doc2 = parser.parseFromString(`<body>${secondary}</body>`, "text/html");

    const existingHrefs = new Set(
      [...doc1.querySelectorAll("li a[href]")].map((a) => a.getAttribute("href")),
    );

    doc2.querySelectorAll("li").forEach((li) => {
      const a = li.querySelector("a[href]");
      if (!a) return;

      const href = a.getAttribute("href");
      if (existingHrefs.has(href)) return;

      const parentList = li.closest("ul, ol");
      const targetList = parentList?.id
        ? doc1.getElementById(parentList.id)
        : doc1.querySelector("ul, ol");

      if (targetList) {
        targetList.appendChild(li.cloneNode(true));
        existingHrefs.add(href);
      }
    });

    return doc1.body.innerHTML;
  }

  async getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const spacedVariant = this.getSpacedVariant(searchTerm);

    try {
      let resultsMarkup;

      if (spacedVariant) {
        // Fetch parallele: termine originale + variante spaziata
        const [result1, result2] = await Promise.allSettled([
          this.fetchMarkup(this.buildSearchUrl(searchTerm)),
          this.fetchMarkup(this.buildSearchUrl(spacedVariant)),
        ]);

        const markup1 = result1.status === "fulfilled" ? result1.value : null;
        const markup2 = result2.status === "fulfilled" ? result2.value : null;

        const has1 = this.markupHasResults(markup1);
        const has2 = this.markupHasResults(markup2);

        if (has1 && has2) {
          // Entrambi hanno risultati: merge, il termine originale fa da base
          resultsMarkup = this.mergeMarkup(markup1, markup2);
        } else if (has2) {
          // Solo la variante spaziata ha trovato qualcosa (caso tipico: "PALL105")
          resultsMarkup = markup2;
        } else if (has1) {
          // Solo il termine originale ha trovato qualcosa
          resultsMarkup = markup1;
        } else {
          // Nessun risultato: mostra il markup vuoto del termine originale
          resultsMarkup = markup1 || markup2;
        }

        if (!resultsMarkup) {
          this.close();
          return;
        }
      } else {
        resultsMarkup = await this.fetchMarkup(this.buildSearchUrl(searchTerm));
      }

      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);